// Why a dashboard row's attention set is not known, in a few words for its
// chip and a sentence for the chip's title.
import type { Cause, Failure } from '../api/failure';
import { clockTime, describeRateLimit, shortTime, type RateLimit } from '../api/rateLimit';

/**
 * A row whose attention could not be worked out: `part` is what failed —
 * reading GitHub's threads and timeline for it, GitHub answering without
 * them (`missing`: a per-node error), Changeling's manual attention
 * changes, or working out the set from them.
 */
export interface AttentionGap {
  part: 'github' | 'missing' | 'overrides' | 'engine';
  failure: Failure;
}

const SHORT: Partial<Record<Cause, string>> = {
  github_slow: 'GitHub too slow',
  github_error: 'GitHub error',
  unreachable: 'server unreachable',
  offline: 'offline',
  server_error: 'server error',
  signed_out: 'signed out',
  no_access: 'no access',
};

const DETAIL: Partial<Record<Cause, string>> = {
  github_slow: 'GitHub took too long to answer',
  github_error: "GitHub couldn't answer just now",
  unreachable: "Changeling's server isn't reachable",
  offline: "you're offline",
  server_error: "Changeling's server ran into a problem",
  signed_out: "you're signed out",
  no_access: "GitHub says you can't see it",
};

/** The reason in a few words: "rate limited until 12:49 PM", "GitHub too slow". */
export function gapReason(g: AttentionGap): string {
  const f = g.failure;
  const l = f.cause === 'rate_limited' ? f.rateLimit : null;
  if (l) {
    const who = l.kind === 'changelist' ? 'Changeling busy' : 'rate limited';
    return l.stated ? `${who} until ${shortTime(l.until)}` : `${who}, checking again about ${shortTime(upToMinute(l.until))}`;
  }
  if (g.part === 'missing') return 'no data from GitHub';
  if (g.part === 'overrides') return "manual changes didn't load";
  if (g.part === 'engine') return 'internal error';
  const short = SHORT[f.cause];
  if (short) return short;
  if (f.cause === 'rate_limited') return 'rate limited';
  if (/SAML/i.test(f.message)) return 'SSO sign-in needed';
  return 'GitHub error';
}

/** `ms` rounded up to the next whole minute (a chip shows minutes only). */
const upToMinute = (ms: number) => Math.ceil(ms / 60_000) * 60_000;

/** When the dashboard checks again by itself, as a fact only when GitHub said when. */
function retryAt(l: RateLimit): string {
  return l.stated
    ? `Changeling checks again by itself at ${clockTime(l.until)}.`
    : `GitHub didn't say for how long; Changeling checks again by itself at about ${clockTime(l.until)}.`;
}

const sentence = (s: string) => (/[.!?…]$/.test(s) ? s : `${s}.`);

/**
 * The chip for a row whose attention is not known: "not checked: …" while a
 * rate limit holds the check back (it runs by itself when the limit lifts),
 * else "couldn't check: …". The title says why in full and what happens next.
 */
export function gapChip(g: AttentionGap): { label: string; title: string } {
  const f = g.failure;
  const l = f.cause === 'rate_limited' ? f.rateLimit : null;
  if (l)
    return {
      label: `not checked: ${gapReason(g)}`,
      title:
        "Changeling hasn't worked out who this pull request is waiting on. " +
        `${describeRateLimit(l, f.at).summary} ${retryAt(l)}`,
    };
  const detail =
    g.part === 'overrides'
      ? `its manual attention changes didn't load (${f.server?.text ?? f.message})`
      : g.part === 'engine'
        ? `working out the attention set failed (${f.message})`
        : (DETAIL[f.cause] ?? f.server?.text ?? f.message);
  return {
    label: `couldn't check: ${gapReason(g)}`,
    title: `Changeling couldn't work out who this pull request is waiting on: ${sentence(detail)} It checks again the next time the dashboard loads.`,
  };
}
